<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  stores: {
    type: Array,
    default: () => [],
  },
  selectedHookId: {
    type: [String, Number],
    default: '',
  },
  showAllStores: {
    type: Boolean,
    default: false,
  },
  displayVariant: {
    type: String,
    default: 'compact',
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isOpen = ref(false);
const isCaptainHeaderVariant = computed(
  () => props.displayVariant === 'captain-header'
);

const normalizedSelectedHookId = computed(
  () => props.selectedHookId?.toString() || ''
);

const availableStores = computed(() => {
  if (!props.showAllStores) {
    return props.stores;
  }

  return [
    {
      id: '',
      label: t('WOOCOMMERCE_PAGES.STORE_SWITCHER.ALL_STORES'),
    },
    ...props.stores,
  ];
});

const activeStore = computed(() => {
  return (
    availableStores.value.find(
      store => store.id === normalizedSelectedHookId.value
    ) || null
  );
});

const activeStoreLabel = computed(() => {
  return (
    activeStore.value?.label || t('WOOCOMMERCE_PAGES.STORE_SWITCHER.ALL_STORES')
  );
});

const isStoreActive = store => {
  return store.id === normalizedSelectedHookId.value;
};

const toggleDropdown = () => {
  if (availableStores.value.length <= 1) {
    return;
  }

  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const handleStoreChange = async store => {
  if (isStoreActive(store)) {
    closeDropdown();
    return;
  }

  const nextParams = {
    ...route.params,
    hookId: store.id || undefined,
  };
  const nextQuery = {
    ...route.query,
    page: undefined,
    store_name: store.id ? store.label : undefined,
  };

  await router.push({
    name: route.name,
    params: nextParams,
    query: nextQuery,
  });

  closeDropdown();
};
</script>

<template>
  <div class="relative">
    <OnClickOutside @trigger="closeDropdown">
      <div class="flex items-center gap-2">
        <span
          :class="[
            isCaptainHeaderVariant
              ? 'text-xl font-medium truncate text-n-slate-12'
              : 'hidden text-sm font-medium text-n-slate-12 lg:inline',
          ]"
        >
          {{ activeStoreLabel }}
        </span>
        <Button
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          :class="[
            isCaptainHeaderVariant
              ? 'rounded-md group-hover:bg-n-slate-3 hover:bg-n-slate-3 [&>span]:size-4'
              : 'rounded-md hover:bg-n-slate-3 [&>span]:size-4',
          ]"
          @click="toggleDropdown"
        />
      </div>

      <div
        v-if="isOpen"
        class="absolute left-0 top-9 z-50 flex w-[calc(100vw-2rem)] max-w-[22rem] flex-col gap-4 rounded-md bg-n-alpha-3 pb-3 pt-5 shadow-md outline outline-1 outline-n-container backdrop-blur-[100px]"
      >
        <div class="flex flex-col gap-1 border-b border-n-alpha-2 px-6 pb-3">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ $t('WOOCOMMERCE_PAGES.STORE_SWITCHER.HEADER') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('WOOCOMMERCE_PAGES.STORE_SWITCHER.SUBTITLE') }}
          </p>
        </div>

        <div class="flex flex-col gap-2 px-4">
          <Button
            v-for="store in availableStores"
            :key="store.id || 'all-stores'"
            variant="ghost"
            color="slate"
            trailing-icon
            :icon="isStoreActive(store) ? 'i-lucide-check' : ''"
            class="h-9 !justify-end !px-2 !py-2 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10"
            size="sm"
            @click="handleStoreChange(store)"
          >
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ store.label }}
            </span>
            <span class="i-lucide-store size-4 text-n-slate-10" />
          </Button>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
